import { useSelector } from 'react-redux'
import getExtensionFromPath from 'helpers/getExtensionFromPath'
import { UploadsTags, usePostApiV1UploadsMutation } from '@wellesley/openapi'
import { UploadFileProps } from '@wellesley/ui'
import { UploadTag } from '../../types'
import checkUploadLimits from '../../helpers/checkUploadLimits'
import { platformSettingsSlice } from '@wellesley/settings/slices/platformSettingsSlice'
import { logger } from 'helpers/logger'

type UploadProps = {
  tag?: UploadTag
  media: UploadFileProps
  onError?: (error?: string) => void
  onUpload?: () => void
  onProgress?: (loaded: number, total: number, percent: number) => void
}

export const useUploadFile = () => {
  const [getUploadUrl] = usePostApiV1UploadsMutation()
  const controller = new AbortController()
  const signal = controller.signal

  const imgPolicy = useSelector(
    platformSettingsSlice.selectors.selectImagePolicy,
  )
  const videoPolicy = useSelector(
    platformSettingsSlice.selectors.selectVideoPolicy,
  )
  const docPolicy = useSelector(platformSettingsSlice.selectors.selectDocPolicy)
  const audioPolicy = useSelector(
    platformSettingsSlice.selectors.selectAudioPolicy,
  )

  const cancelUpload = () => controller.abort('Upload canceled')

  const uploadFile = async ({
    media,
    tag,
    onError,
    onUpload,
    onProgress,
  }: UploadProps) => {
    const extension = getExtensionFromPath(media.file.name)

    const error = await checkUploadLimits({
      imgPolicy,
      docPolicy,
      videoPolicy,
      audioPolicy,
      media,
    })

    if (error) {
      onError?.(error)
      return
    }

    try {
      const uploadData = await getUploadUrl({
        createUpload: {
          extension: extension as string,
          tags: [tag as unknown as UploadsTags], // backend type is 'Post' | 'Avatar' | 'Header' | 'Album'
          meta: {
            name: media.file.name,
          },
        },
      })

      if ('error' in uploadData) {
        throw new Error(uploadData.error as string)
      }

      const uploadUrl = uploadData.data?.uri

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl)
        xhr.timeout = 10 * 60 * 1000
        if (signal) {
          signal.addEventListener('abort', () => {
            try {
              xhr.abort()
            } catch (e) {
              logger.warn('Cancel upload failed', e)
            }
          })
        }
        xhr.ontimeout = () => reject(new Error('Upload timed out'))
        xhr.upload.onprogress = (evt: ProgressEvent<EventTarget>) => {
          if (!evt.lengthComputable) return
          const percent = Math.round((evt.loaded / evt.total) * 100)

          onProgress?.(evt.loaded, evt.total, percent)
        }
        xhr.onerror = () => reject(new Error('Upload failed'))
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(xhr.statusText))
        }
        xhr.send(media.file)
      })

      onUpload?.()
      return uploadData.data.upload.uploadId
    } catch (e) {
      const err = String(e)
      onError?.(err)
    }
  }

  return [uploadFile, cancelUpload] as const
}
